<template>
    <div class="confirmation">
        <div
            class="button button-primary button-buy"
            v-if="modelLoaded && !modelError"
            v-on:click="showModal()"
        >
            {{ locale("ripe_retail.confirmation.purchase") }}
        </div>
        <h3 class="price-text">{{ priceFinal | money(priceCurrency) }}</h3>
        <modal name="confirmation">
            <h3 class="title">{{ locale("ripe_retail.confirmation.order_confirmation") }}</h3>
            <div class="summary">
                <div class="summary-left-top">
                    <img class="image summary-image" ref="image" />
                    <div class="summary-details">
                        <h3>{{ locale(modelText) }}</h3>
                        <p>
                            {{ locale("ripe_retail.confirmation.size_colon") }}
                            <span class="summary-size">{{ sizeText }}</span>
                        </p>
                    </div>
                </div>
                <div class="summary-right summary-info">
                    <div class="message">
                        <span v-if="!confirmed">
                            {{ locale("ripe_retail.confirmation.assist_confirm") }}
                        </span>
                        <div class="customer" v-if="!confirmed && showCustomerIdInput">
                            <span>{{ locale("ripe_retail.confirmation.customer") }}</span>
                            <input type="text" v-model="customerId" />
                        </div>
                        <div class="success" v-if="confirmed">
                            <p class="label">
                                {{ locale("ripe_retail.confirmation.your_order_number") }}
                            </p>
                            <p class="order-number">
                                {{ locale("ripe_retail.confirmation.order") }} #{{ orderNumber }}
                            </p>
                            <p class="help">
                                {{ locale("ripe_retail.confirmation.assist_pay") }}
                            </p>
                            <p class="label label-customer" v-if="showCustomerIdInput">
                                {{ locale("ripe_retail.confirmation.customer") }} {{ customerId }}
                            </p>
                        </div>
                    </div>
                    <div class="price">
                        <span class="price-label">
                            {{ locale("ripe_retail.confirmation.total") }}
                        </span>
                        <span class="price-value">{{ priceFinal | money(priceCurrency) }}</span>
                    </div>
                </div>
                <div class="summary-left-bottom">
                    <div class="customization-details">
                        <p class="details-title">
                            {{ locale("ripe_retail.confirmation.customization_details_title") }}:
                        </p>
                        <div v-if="hasCustomization">
                            <div v-for="(value, key) in customization" v-bind:key="key">
                                <div class="key">
                                    {{ localeModel(key) }} -
                                </div>
                                <div class="value">
                                    {{ localeModel(key, value.material) }}
                                    {{ localeModel(key, value.material, value.color) }}
                                </div>
                            </div>
                        </div>
                        <div class="value" v-else>
                            {{ locale("ripe_retail.confirmation.no_customization") }}
                        </div>
                    </div>
                    <div class="personalization-details">
                        <p class="details-title">
                            {{ locale("ripe_retail.confirmation.personalization_details_title") }}:
                        </p>
                        <div v-if="hasPersonalization">
                            <div
                                class="initials"
                                v-for="(value, group) in initialsText"
                                v-bind:key="group"
                            >
                                <div class="key">
                                    {{
                                        locale(`ripe_commons.${brand}.confirmation.group.${group}`)
                                    }}
                                </div>
                                <div class="value">
                                    - {{ value }}
                                </div>
                            </div>
                            <div class="engraving">
                                <div class="key">
                                    {{ locale("ripe_retail.confirmation.finishing") }}
                                </div>
                                <div class="value">
                                    - {{ engravingText }}
                                </div>
                            </div>
                        </div>
                        <div class="value" v-else>
                            {{ locale("ripe_retail.confirmation.no_personalization") }}
                        </div>
                    </div>
                </div>
            </div>
            <div class="buttons-container">
                <div class="button button-cancel" v-if="!confirmed" v-on:click="hideModal()">
                    {{ locale(cancelText) }}
                </div>
                <div
                    class="button button-primary button-apply"
                    v-bind:class="{ loading }"
                    v-on:click="apply()"
                >
                    {{ locale(buyText) }}
                </div>
            </div>
        </modal>
    </div>
</template>

<style scoped>
.summary {
    font-size: 0px;
    margin-bottom: 30px;
    text-align: center;
}

.summary > .summary-left-top {
    border-bottom: 1px solid #e9eaee;
    display: inline-block;
    margin: 0px 70px 10px 0px;
    padding-bottom: 12px;
    width: 300px;
}

.summary > .summary-left-top > .summary-image {
    height: 300px;
    width: 100%;
}

.summary > .summary-left-top > .summary-details {
    margin-top: -50px;
}

.summary > .summary-left-top > .summary-details > h3 {
    font-size: 15px;
    font-weight: normal;
    height: 20px;
    line-height: 20px;
    margin: 0px 0px 10px 0px;
}

.summary > .summary-left-top > .summary-details > p {
    color: #888888;
    font-size: 12px;
    height: 16px;
    line-height: 16px;
    margin: 0px 0px 3px 0px;
}

.summary > .summary-left-top > .summary-details > p > span {
    color: #000000;
}

.summary > .summary-left-bottom {
    margin-right: 70px;
    text-align: left;
}

.summary > .summary-left-bottom .details-title {
    color: #888888;
    font-size: 10px;
    font-stretch: normal;
    font-style: normal;
    font-weight: bold;
    height: 11px;
    letter-spacing: normal;
    line-height: normal;
    margin: 0px 0px 8px 0px;
}

.summary > .summary-left-bottom .customization-details {
    color: #888888;
    display: inline-block;
    font-size: 11px;
    line-height: 16px;
    margin: 0px 30px 3px 0px;
    min-width: 140px;
}

.summary > .summary-left-bottom .personalization-details {
    color: #888888;
    display: inline-block;
    font-size: 11px;
    line-height: 16px;
    margin: 0px 0px 3px 0px;
    min-width: 140px;
    vertical-align: top;
}

.summary > .summary-left-bottom .personalization-details .initials .value {
    text-transform: uppercase;
}

.summary > .summary-left-bottom .key {
    color: #888888;
    display: inline-block;
    font-size: 10px;
    height: 16px;
    line-height: 16px;
    margin: 0px 0px 3px 0px;
    text-transform: capitalize;
}

.summary > .summary-left-bottom .value {
    color: #000000;
    display: inline-block;
    font-size: 10px;
    height: 16px;
    line-height: 16px;
    margin: 0px 0px 3px 0px;
    text-transform: capitalize;
}

.summary > .summary-info {
    display: inline-block;
    margin-bottom: 10px;
    vertical-align: bottom;
    width: 300px;
}

.summary > .summary-info .message {
    border: 1px solid #e9eaee;
    border-top-left-radius: 4px;
    border-top-right-radius: 4px;
    color: #d0021b;
    font-size: 15px;
    height: 200px;
    position: relative;
    text-align: center;
}

.summary > .summary-info .message > span {
    left: 40px;
    line-height: 20px;
    position: absolute;
    right: 40px;
    top: 50%;
    transform: translateY(-50%);
}

.summary .success {
    box-sizing: border-box;
    line-height: normal;
    max-height: 100%;
    padding: 30px 60px 30px 60px;
    text-align: center;
}

.summary .success > .label {
    color: #888888;
    font-size: 12px;
    font-weight: 500;
    margin: 0px 0px 30px 0px;
    text-transform: uppercase;
}

.summary .success > .label.label-customer {
    text-transform: none;
}

.summary .success > .order-number {
    color: #000000;
    font-size: 25px;
    font-weight: bold;
    line-height: 25px;
    margin: 30px 0px 30px 0px;
    text-transform: uppercase;
}

.summary .success > .help {
    color: #9b9b9b;
    font-size: 12px;
    line-height: 16px;
    text-align: center;
}

.summary > .summary-info .price {
    background-color: #f5f5f5;
    border-bottom-left-radius: 20px;
    border-bottom-right-radius: 20px;
    height: 60px;
    line-height: 60px;
    padding: 0px 24px 0px 24px;
}

.summary > .summary-info .price .price-label {
    float: left;
    font-size: 15px;
}

.summary > .summary-info .price .price-value {
    color: #222222;
    float: right;
    font-size: 19px;
    font-weight: bold;
}

.summary .customer {
    bottom: 20px;
    left: 20px;
    line-height: 14px;
    margin: 0px auto;
    position: absolute;
    right: 20px;
}

.summary .customer span {
    color: #000000;
    font-size: 14px;
    font-weight: bold;
}

.summary .customer input {
    border-bottom: 1px solid #000000;
    border-width: 0px 0px 1px 0px;
    font-size: 14px;
    margin-left: 4px;
    outline: 0px;
}

.price-text {
    float: right;
    font-size: 25px;
    font-weight: normal;
    height: 50px;
    letter-spacing: 0.9px;
    margin: 0px 16px 0px 0px;
}

.button.button-buy {
    float: right;
}

@media screen and (max-height: 1340px) {

    .confirmation ::v-deep .button.button-primary.button-buy {
        font-size: 16px;
        height: 40px;
        line-height: 40px;
    }
}

@media screen and (max-width: 1024px) {

    .confirmation ::v-deep .button.button-primary.button-buy {
        width: 28vw;
    }
}
</style>

<script>
import { localeMixin, modalMixin } from "ripe-commons-pluginus";

export const Confirmation = {
    name: "confirmation",
    mixins: [modalMixin, localeMixin],
    props: {
        orderUrl: {
            type: String,
            default: null,
            required: true
        }
    },
    data: function() {
        return {
            loading: false,
            confirmed: false,
            orderNumber: null,
            customerId: this.$store.state.customerId,
            modalOptions: {
                name: "confirmation",
                backgroundLeave: false
            }
        };
    },
    computed: {
        priceFinal() {
            return this.$store.getters.priceFinal;
        },
        priceCurrency() {
            return this.$store.getters.priceCurrency || "EUR";
        },
        modelText() {
            return this.description;
        },
        sizeText() {
            const size = this.$store.state.size;
            return size && size.sizeText;
        },
        initialsExtra() {
            const personalization = this.$store.state.personalization;
            return personalization ? personalization.initialsExtra : {};
        },
        engraving() {
            let engraving = "";
            if (this.initialsExtra) {
                for (const name in this.initialsExtra) {
                    const group = this.initialsExtra[name];
                    engraving =
                        group.initials.length && group.engraving ? group.engraving : engraving;
                }
            }
            return engraving;
        },
        initialsText() {
            const initials = {};
            for (const group in this.initialsExtra) {
                const text = this.initialsExtra[group].initials;
                if (!text.length || text === "$blank") {
                    continue;
                }
                initials[group] = text;
            }
            return initials;
        },
        engravingText() {
            return this.localeEngraving(this.engraving);
        },
        hasCustomization() {
            return Boolean(this.customization && Object.keys(this.customization).length > 0);
        },
        hasPersonalization() {
            for (const group in this.initialsExtra) {
                if (this.initialsExtra[group].initials.length) {
                    return true;
                }
            }
            return false;
        },
        customization() {
            const parts = {};
            const stateParts = this.$store.state.parts;
            const defaults = this.$store.state.defaults;
            for (const statePartName in stateParts) {
                const defaultPart = defaults[statePartName] || {};
                if (defaultPart.hidden && defaultPart.hidden === true) {
                    continue;
                }
                const part = Object.assign({}, stateParts[statePartName]);
                if (!part.material) {
                    part.material = "no_" + statePartName;
                    part.color = "";
                }
                parts[statePartName] = part;
            }
            return parts;
        },
        showCustomerIdInput() {
            return this.customerIdInput;
        },
        buyText() {
            if (this.loading) {
                return "ripe_retail.confirmation.processing_order";
            } else if (this.confirmed) {
                return "ripe_retail.confirmation.ok";
            } else {
                return "ripe_retail.confirmation.purchase";
            }
        },
        cancelText() {
            return this.confirmed
                ? "ripe_retail.modal.cancel"
                : "ripe_retail.confirmation.back_customization";
        },
        hasSize() {
            // the size is valid if either no size is needed because
            // the model doesn't support it, or if the size is defined
            return (
                !this.$store.state.hasSize ||
                (this.$store.state.size && this.$store.state.size.size)
            );
        }
    },
    watch: {
        orderNumber(value) {
            this.$store.commit("orderNumber", value);
        },
        customerId(value) {
            this.$store.commit("customerId", value);
        }
    },
    mounted: function() {
        this.$bus.bind("refresh", () => {
            this.parts = null;
        });

        this.image = this.$ripe.bindImage(this.$refs.image, {
            noPrice: true
        });
    },
    destroyed: async function() {
        if (this.image) await this.$ripe.unbindImage(this.image);
        this.image = null;
    },
    methods: {
        createOrder() {
            const order = this.$store.getters.getOrderInfo();
            for (const part in order.parts) {
                const value = order.parts[part];
                if (!value || !value.color) {
                    delete order.parts[part];
                }
            }
            this.loading = true;
            fetch(this.orderUrl, {
                body: JSON.stringify(order),
                method: "POST"
            })
                .then(response => response.json())
                .then(response => {
                    this.loading = false;
                    this.confirmed = response.status === "success";
                    this.orderNumber = response.order_number;
                    this.confirmed && this.$bus.trigger("purchase", this.orderNumber);
                });
        },
        willShowModal(initial = true) {
            // if there shouldn't be a size for the model or it is already defined,
            // moves forwards and show the confirmation modal (as expected)
            if (this.hasSize) {
                return true;
            }
            // otherwise and in case this is the first try shows the size modal
            // to ensure proper selection of the size
            else if (initial) {
                this.$bus.trigger("open_size", () => this.showModal(false));
                return false;
            }
            return false;
        },
        modalHidden() {
            this.loading = false;
            this.confirmed = false;
            this.orderNumber = null;
            this.customerId = null;
        },
        apply() {
            return this.confirmed ? this.hideModal() : this.createOrder();
        }
    }
};

export default Confirmation;
</script>
